import {
  BaseQueryApi,
  createApi,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query/react";
import { RootState } from "../store";
import { refreshSession, getCurrentUser } from "../cognitoConfig";
import { updateIdToken, clearSession } from "../slices/sessionSlice";
import { togglePopup, setUpgradeModalOpen, setAuthPromptOpen } from "../slices/utilsSlice";
import { addToast } from "../slices/toastSlice";
import i18n from "../translations/i18n";

// Mutex to prevent concurrent token refreshes
let refreshPromise: Promise<string | null> | null = null;

// Global flag to suppress toasts during bulk operations
let _suppressToasts = false;
export const suppressToasts = (value: boolean) => { _suppressToasts = value; };

const rawBaseQuery = fetchBaseQuery({
  baseUrl: `${import.meta.env.VITE_API_URL}/api/v1`,
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as RootState;
    const token = state.session.idToken;
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    const targetUid = state.impersonation.targetUid;
    if (targetUid) {
      headers.set("X-Impersonate-Uid", targetUid);
    }
    return headers;
  },
});

const baseQueryWithReauth = async (
  args: Parameters<typeof rawBaseQuery>[0],
  api: BaseQueryApi,
  extraOptions: unknown
) => {
  // First attempt with current token
  let result = await rawBaseQuery(args, api, extraOptions);

  // If we get a 401, attempt to refresh the token (with mutex to prevent concurrent refreshes)
  if (result.error && result.error.status === 401) {
    const state = api.getState() as RootState;
    const hadToken = !!state.session.idToken;

    // If user was never authenticated (anonymous browsing), show auth prompt
    // But only if session loading is complete — don't trigger during initial hydration
    if (!hadToken) {
      if (!state.session.loading) {
        api.dispatch(setAuthPromptOpen(true));
      }
      return result;
    }

    try {
      // If another request is already refreshing, wait for it
      if (!refreshPromise) {
        refreshPromise = (async () => {
          const cognitoUser = getCurrentUser();
          if (cognitoUser) {
            const session = await refreshSession();
            return session.getIdToken().getJwtToken();
          }
          return null;
        })();
      }

      let freshToken: string | null = null;
      try {
        freshToken = await refreshPromise;
      } finally {
        refreshPromise = null;
      }

      if (freshToken) {
        // Update the token in Redux
        api.dispatch(updateIdToken(freshToken));

        // Retry the original query with the new token
        result = await rawBaseQuery(args, api, extraOptions);
      } else {
        // No current user - they need to log in
        api.dispatch(clearSession());
      }
    } catch {
      refreshPromise = null;
      api.dispatch(clearSession());

      // Show error popup - the user needs to log in again
      api.dispatch(
        togglePopup({
          open: true,
          type: "Error",
          message: i18n.t("toast.sessionExpired"),
        })
      );
    }
  }

  // Allow callers to suppress all toasts (e.g., bulk operations)
  const suppressToast = _suppressToasts || (extraOptions as { suppressToast?: boolean })?.suppressToast;

  // Success notification handling
  if (!suppressToast && result.data && typeof result.data === "object") {
    const method =
      (args as { method?: string })?.method?.toUpperCase() || "GET";
    if (method !== "GET") {
      try {
        const url = (args as { url?: string })?.url || "";
        const resourceName = formatResourceName(url);

        // Suppress entirely for uploads and vocab helper routes (not CRUD)
        const SUPPRESSED_RESOURCES = ["upload"];
        const normalizedUrl = url.startsWith("/") ? url.slice(1) : url;
        const SUPPRESSED_VOCAB_ROUTES = [
          "vocab/check-duplicate",
          "vocab/enrich-word",
          // Bulk import fires its own summary toast from the calling component
          "vocab/my/bulk-import",
        ];
        const isSuppressedVocab = SUPPRESSED_VOCAB_ROUTES.some(r => normalizedUrl.startsWith(r))
          || /^vocab\/my\/\d+\//.test(normalizedUrl); // review, revert-srs

        // Custom toast for AI translate — only on cache miss (full generation pipeline ran)
        if (normalizedUrl.startsWith("ai/translate")) {
          const data = result.data as Record<string, unknown> | undefined;
          if (!data?.cache_hit) {
            api.dispatch(addToast({ message: i18n.t("toast.wordTranslated"), type: "success" }));
          }
          return result;
        }

        // Suppress other AI routes (chat, etc.)
        if (normalizedUrl.startsWith("ai/")) {
          return result;
        }

        // Suppress tour-state updates (fire-and-forget)
        if (normalizedUrl.startsWith("users/me/tour-state")) {
          return result;
        }

        // Suppress user profile updates — the profile page dispatches its own toast
        if (/^users\/\d+$/.test(normalizedUrl) && method === "PUT") {
          return result;
        }

        // Suppress messages routes (markAsRead, sendMessage)
        if (normalizedUrl.startsWith("messages/")) {
          return result;
        }

        // Suppress bug-reports (has its own success state)
        if (normalizedUrl.startsWith("bug-reports")) {
          return result;
        }

        if (SUPPRESSED_RESOURCES.includes(resourceName?.toLowerCase()) || isSuppressedVocab) {
          return result;
        }

        // Skip popup for language-only user updates (from language selector)
        const body = (args as { body?: Record<string, unknown> })?.body;
        if (body && Object.keys(body).length === 1 && "preferred_language" in body) {
          return result;
        }

        // Custom message for vocab add/remove
        const isVocabRoute = normalizedUrl.startsWith("vocab/");
        const message = isVocabRoute
          ? method === "DELETE"
            ? i18n.t("toast.wordRemoved")
            : i18n.t("toast.wordAdded")
          : getDefaultSuccessMessage(method, resourceName);
        api.dispatch(addToast({ message, type: "success" }));
      } catch {
        // Error handling success response
      }
    }
  }

  // Error popup handling
  if (!suppressToast && result.error && result.error.status !== 401) {
    // Skip 401s - handled above
    const reqMethod = (args as { method?: string })?.method?.toUpperCase() || "GET";

    // Silently ignore transient errors on GET requests (server restart / downtime)
    if (reqMethod === "GET") {
      const s = result.error.status;
      // 502/503 from reverse proxy, FETCH_ERROR (network down), PARSING_ERROR (HTML instead of JSON)
      if (s === 502 || s === 503 || s === "FETCH_ERROR" || s === "PARSING_ERROR") {
        return result;
      }
      // Suppress errors on resolve endpoints — these are handled by the component
      // (e.g. stale slug after reorder triggers a refetch that 404s,
      // or 403 access denied for paid courses handled by the page)
      const url = (args as { url?: string })?.url || "";
      if ((s === 404 || s === 403) && url.includes("/resolve/")) {
        return result;
      }
    }

    // Suppress errors on auth routes — handled by the component (invite modal, etc.)
    const url = (args as { url?: string })?.url || "";
    const normalizedUrl = url.startsWith("/") ? url.slice(1) : url;
    // Suppress TTS audio generation errors — handled silently by the Flashcard component
    if (normalizedUrl.match(/^vocab\/words\/\d+\/generate-audio$/)) {
      return result;
    }
    if (normalizedUrl.startsWith("auth/signup") || normalizedUrl.startsWith("auth/resend-invite")) {
      return result;
    }

    // Check if this is a premium-limit error → show upgrade modal instead of error popup
    if ("data" in result.error && isPremiumLimitError(result.error.data)) {
      api.dispatch(setUpgradeModalOpen(true));
      return result;
    }

    let errorMessage = i18n.t("serverErrors.generic", "An error occurred");
    let errorType = "error";

    // Try to get message from error response data
    if ("data" in result.error) {
      const errorData = result.error.data;

      // Handle JSON error responses
      if (typeof errorData === 'object' && errorData !== null) {
        const errorResponse = errorData as { error?: string | { code?: string; message?: string }; message?: string };
        // error can be a string or nested { code, message } object
        if (typeof errorResponse.error === 'string') {
          errorMessage = errorResponse.error;
        } else if (typeof errorResponse.error === 'object' && errorResponse.error?.message) {
          errorMessage = errorResponse.error.message;
        } else if (errorResponse.message) {
          errorMessage = errorResponse.message;
        }
      }
      // Handle HTML error responses (extract text from <pre> tag)
      else if (typeof errorData === 'string') {
        // Try to extract error message from HTML
        const preMatch = errorData.match(/<pre>([\s\S]*?)<\/pre>/);
        if (preMatch) {
          // Decode HTML entities and extract first line (actual error message)
          let htmlContent = preMatch[1];
          // Decode common HTML entities
          htmlContent = htmlContent
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&#39;/g, "'")
            .replace(/&quot;/g, '"')
            .replace(/<br\s*\/?>/gi, '\n');

          // Extract the first line (the actual error message)
          const firstLine = htmlContent.split('\n')[0].trim();
          if (firstLine.startsWith('Error: ')) {
            errorMessage = firstLine.substring(7); // Remove "Error: " prefix
          } else if (firstLine) {
            errorMessage = firstLine;
          }
        }
        // Fallback: if it's a plain text error message
        else if (errorData.trim() && !errorData.includes('<html>')) {
          errorMessage = errorData.trim();
        }
      }

      // Map status codes to appropriate error types
      switch (result.error.status) {
        case 400: // BAD_REQUEST
          errorType = "warning";
          break;
        case 404: // NOT_FOUND
          errorType = "warning";
          errorMessage = errorMessage || i18n.t("serverErrors.resourceNotFound", "Resource not found");
          break;
        case 403: // FORBIDDEN
          errorType = "error";
          break;
        default:
          errorType = "error";
      }
    }

    // Translate known server error messages
    errorMessage = translateServerError(errorMessage);

    api.dispatch(
      togglePopup({
        open: true,
        type: errorType,
        message: errorMessage,
      })
    );
  }

  return result;
};

// Helper functions
const formatResourceName = (url: string): string => {
  try {
    if (!url) return i18n.t("toast.resources.resource");
    const segments = url.split("/").filter(Boolean);
    if (!segments.length) return i18n.t("toast.resources.resource");
    const key = segments[0].toLowerCase();
    const translated = i18n.t(`toast.resources.${key}`, { defaultValue: "" });
    if (translated) return translated;
    // Fallback: capitalize and strip trailing "s"
    const capitalized = key.charAt(0).toUpperCase() + key.slice(1);
    return capitalized.endsWith("s") ? capitalized.slice(0, -1) : capitalized;
  } catch {
    return i18n.t("toast.resources.resource");
  }
};

const getDefaultSuccessMessage = (
  method: string,
  resourceName: string
): string => {
  try {
    switch (method?.toUpperCase() || "UNKNOWN") {
      case "POST":
        return i18n.t("toast.resourceCreated", { resource: resourceName });
      case "PUT":
      case "PATCH":
        return i18n.t("toast.resourceUpdated", { resource: resourceName });
      case "DELETE":
        return i18n.t("toast.resourceDeleted", { resource: resourceName });
      default:
        return i18n.t("toast.operationSuccess");
    }
  } catch {
    return i18n.t("toast.operationSuccess");
  }
};

// Detect errors that can be resolved by upgrading to premium.
// These should show the upgrade modal instead of an error popup.
const PREMIUM_LIMIT_ERRORS = [
  "Attempt limit reached",
  "Grammar check limit reached for this exercise",
  "Premium subscription required",
];

const isPremiumLimitError = (errorData: unknown): boolean => {
  if (typeof errorData !== 'object' || errorData === null) return false;
  const data = errorData as Record<string, unknown>;

  // Server sends premium_required: true for premium-gated content
  if (data.premium_required === true) return true;

  // Server sends is_premium: false for limit-based errors (attempts, grammar checks)
  // where upgrading would raise the limit
  const errorMsg = typeof data.error === 'string' ? data.error : '';
  if (PREMIUM_LIMIT_ERRORS.includes(errorMsg) && data.is_premium !== true) return true;

  return false;
};

// Map server error messages to i18n keys for localization
const SERVER_ERROR_MAP: Record<string, string> = {
  // Generic / not-found
  "An error occurred": "serverErrors.generic",
  "Resource not found": "serverErrors.resourceNotFound",
  "User not authenticated": "serverErrors.notAuthenticated",
  "User not found": "serverErrors.userNotFound",
  "Course not found": "serverErrors.courseNotFound",
  "Module not found": "serverErrors.moduleNotFound",
  "Exercise not found": "serverErrors.exerciseNotFound",
  "Category not found": "serverErrors.categoryNotFound",
  "Review not found": "serverErrors.reviewNotFound",
  "Transaction not found": "serverErrors.transactionNotFound",
  "Attempt not found": "serverErrors.attemptNotFound",
  "Attempt not found or unauthorized": "serverErrors.attemptNotFound",
  "User account not found": "serverErrors.accountNotFound",
  "File not found": "serverErrors.fileNotFound",
  "Word not found": "serverErrors.wordNotFound",
  "Validation error": "serverErrors.validationError",
  "Bug report not found": "serverErrors.bugReportNotFound",
  // Reviews
  "You must be enrolled in this course to leave a review": "serverErrors.mustBeEnrolledToReview",
  "You have already reviewed this course": "serverErrors.alreadyReviewed",
  "Invalid review data": "serverErrors.invalidReviewData",
  "Not authorized to update this review": "serverErrors.notAuthorizedReview",
  "Not authorized to delete this review": "serverErrors.notAuthorizedReview",
  // Enrollment / progress
  "Already enrolled in this course": "serverErrors.alreadyEnrolled",
  "User already enrolled in this course": "serverErrors.alreadyEnrolled",
  // Payments
  "This course is free and does not require purchase": "serverErrors.courseFreeNoPurchase",
  "You have already purchased this course": "serverErrors.alreadyPurchased",
  "Course package has no price set": "serverErrors.noPriceSet",
  "No active subscription found": "serverErrors.noActiveSubscription",
  // Messages
  "You can only access your own conversation": "serverErrors.onlyOwnConversation",
  "You can only send messages in your own conversation": "serverErrors.onlyOwnMessages",
  "You can only mark your own conversation as read": "serverErrors.onlyMarkOwnRead",
  // Users / profile
  "You can only view your own stats": "serverErrors.onlyViewOwnStats",
  "You can only delete your own avatar": "serverErrors.onlyDeleteOwnAvatar",
  "Email already in use": "serverErrors.emailAlreadyInUse",
  "Email already in use by another user": "serverErrors.emailAlreadyInUse",
  "User has no email address": "serverErrors.userNoEmail",
  "A user with this email already exists": "serverErrors.emailAlreadyExists",
  // Auth
  "Invalid or expired invitation token": "serverErrors.invalidInvitationToken",
  "Email is required": "serverErrors.emailRequired",
  "ID token is required": "serverErrors.tokenRequired",
  "ID token and new password are required": "serverErrors.tokenAndPasswordRequired",
  "Token is required": "serverErrors.tokenRequired",
  "Token and password are required": "serverErrors.tokenAndPasswordRequired",
  "Email, confirmation code, and new password are required": "serverErrors.resetFieldsRequired",
  // Uploads
  "No file provided": "serverErrors.noFileProvided",
  // Vocab
  "Word not in your vocabulary": "serverErrors.wordNotInVocabulary",
  // Attempts
  "No attempts found for this exercise": "serverErrors.noAttemptsFound",
  "Unauthorized to delete this attempt": "serverErrors.unauthorizedDeleteAttempt",
  "You are not authorized to delete this attempt": "serverErrors.unauthorizedDeleteAttempt",
  // Rate limiting
  "Too many requests, please try again later": "serverErrors.tooManyRequests",
  "Too many AI messages. Please wait a moment.": "serverErrors.tooManyAIMessages",
  "Too many transcription requests. Please wait a moment.": "serverErrors.tooManyTranscriptions",
  "Too many translation requests. Please wait a moment.": "serverErrors.tooManyTranslations",
  "Too many requests. Sign in for higher limits.": "serverErrors.tooManyRequestsSignIn",
  "Too many check requests. Please wait a moment.": "serverErrors.tooManyCheckRequests",
  "Too many messages. Please wait a minute before sending more.": "serverErrors.tooManyMessages",
  "Too many upload requests, please try again later": "serverErrors.tooManyUploads",
  "Too many bulk requests, please try again later": "serverErrors.tooManyBulkRequests",
  // Auth
  "Password does not meet requirements": "serverErrors.passwordRequirements",
  "Failed to create user": "serverErrors.failedToCreateUser",
  "Unauthorized: Invalid token": "serverErrors.invalidToken",
  "This action is not allowed while impersonating a user": "serverErrors.impersonationBlocked",
  // Course access
  "Course purchase required": "serverErrors.coursePurchaseRequired",
  // Premium limits (these are intercepted before reaching here, but mapped for completeness)
  "Attempt limit reached": "serverErrors.attemptLimitReached",
  "Grammar check limit reached for this exercise": "serverErrors.grammarCheckLimitReached",
  "Premium subscription required": "serverErrors.premiumRequired",
};

const translateServerError = (message: string): string => {
  const key = SERVER_ERROR_MAP[message];
  if (key) {
    const translated = i18n.t(key, { defaultValue: "" });
    if (translated) return translated;
  }
  return message;
};

export const baseApi = createApi({
  reducerPath: "baseApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Course", "Exercise", "Module", "Category", "Attempt", "Courses", "Transaction", "Analytics", "AdminStats", "Reviews", "MyReviews", "Conversations", "Messages", "UnreadCount", "Subscription", "Notifications", "NotificationUnread", "AIChatMessages", "AIChatSessions", "AIChatUsage", "Vocab", "UserVocab", "VocabDue", "UserActivity", "UserDetail", "CachedWords", "CachedSentences", "AllVocabWords", "BugReports", "ContentSync", "Users"],
  endpoints: () => ({}),
});
